// Sub Admin manual. Shares AdminLayout with Company Admin but only sees the
// operations the Company Admin has delegated. Three areas are never available.

const manual = {
  key: 'sub_admin',
  role: 'Sub Admin',
  title: 'User Manual — Sub Admin',
  subtitle:
    'For delegated administrators: help run the company within the specific areas your Company Admin has granted you.',
  sections: [
    {
      id: 'welcome',
      title: 'Welcome',
      blocks: [
        'A Sub Admin uses the same admin panel as the Company Admin, but sees only the areas you have been granted. Your Company Admin ticks specific operations for you. Each one unlocks a matching part of the sidebar and its pages. If you have not been granted anything yet, your dashboard says so and points you to your profile.',
        {
          note: 'Three areas belong to the Company Admin and can never be delegated to a Sub Admin: Billing, AI Provider Keys and SOP Integration. The menu items are hidden, and opening those URLs directly returns a "not allowed" response.',
        },
      ],
    },
    {
      id: 'page-guide',
      title: 'Page-by-Page Guide',
      blocks: [
        'This guide walks the pages a Sub Admin can reach, in sidebar order. They are the SAME pages a Company Admin uses — same buttons, forms and actions. Each appears only if your Work Role grants the operation named in its heading. Three owner-only areas (Billing, AI Provider Keys and SOP Integration) never appear and are blocked on the server.',

        { sub: 'Dashboard (/dashboard)' },
        { image: '/manual/ca-dashboard.png', caption: 'Dashboard — a summary based on what you have been granted; each panel appears only if you hold the matching operation.' },
        'Your Dashboard is the first page after login. It mirrors the Company Admin dashboard, but each panel appears only for the operations you hold.',
        {
          list: [
            'KPI strip (up to four tiles, by grant): To Do (task monitor), Reports Compliance · Today (work reports), Active Members (members management), SOPs Total (SOPs).',
            'Action Needed: At-Risk Tasks (with task monitor) and Missing Reports Today (with work reports).',
            'Workload: one chart from your strongest grant — Tasks by Status, compliance rings, SOP usage, or Members by Work Role.',
            'Recent Activity: only the event types you are allowed to see.',
            'Quick Actions: shortcuts for the operations you hold.',
          ],
        },
        'If you hold no operations, the dashboard shows "No operations assigned yet" with a link to your profile.',

        { sub: 'Administration › Departments (departments.manage)' },
        { image: '/manual/ca-departments.png', caption: 'Administration › Departments — create and organise the departments that scope Spaces, reports and workflows.' },
        'Create and organise departments. Departments scope Spaces, reports and workflows.',
        {
          list: [
            'Add Department — opens the form.',
            'Row actions: Edit; Delete (confirmation required).',
            'Filters: search by name/description, a Workspace dropdown (multi-workspace tenants only), and Clear all.',
          ],
        },
        'Add / Edit Department form: Department Name (required, 2–120 chars, must contain a letter), Description (optional, ≤500 chars), Status (Active/Inactive), Workspaces (multi-workspace tenants only). When editing across workspaces, an "Apply these changes to matching Departments in other Workspaces" checkbox also appears.',

        { sub: 'Administration › Work Roles (departments.manage)' },
        { image: '/manual/ca-work-roles.png', caption: 'Administration › Work Roles — tick operations to define a role, and set the seniority order via Arrange Hierarchy.' },
        'Define custom roles by ticking operations, and set the seniority order. Two tabs: Roles and Analytics.',
        {
          list: [
            'Add Work Role — opens the form.',
            'Arrange Hierarchy — drag roles from Most senior to Most junior (appears once you have two or more roles); Save Hierarchy to persist.',
            'Row actions: Edit; Delete (blocked while members are assigned — reassign them first).',
          ],
        },
        'Add / Edit Work Role form: Work Role Name (required, must contain a letter), Description (optional), and an Access level choice (Self only / Department / Company-wide) that derives from the operations you tick. An Advanced section shows the full operations matrix, grouped by module, with a Select-all per module and a live "N operations selected" count.',

        { sub: 'Administration › Members (members.manage)' },
        { image: '/manual/ca-members.png', caption: 'Administration › Members — invite people and manage them; each member carries a Work Role and a Department.' },
        'Invite, view and manage everyone in the company. A List view (editable table) and a Grid view (read-only task-signal cards) share the same toolbar.',
        {
          list: [
            'Add Member — opens the invite form (top right).',
            'Row actions: Edit; Reset password (shows a one-time temporary password); Resend invitation (for a pending invite); Offboard (records a destination and is reversible) or Re-onboard; Suspend / re-activate.',
            'Filters: search by Full Name or Email ID, plus Department and Work Role dropdowns, and Clear all.',
            'List / Grid view toggle.',
          ],
        },
        'Add / Edit Member form:',
        {
          table: {
            head: ['Field', 'Notes'],
            rows: [
              ['Full Name', 'Required; 2–80 characters; must contain a letter.'],
              ['Email ID', 'Required; valid email (stored lower-case).'],
              ['Contact Number', 'Optional; validated phone number.'],
              ['Role', 'User or Sub Admin.'],
              ['Work Role', 'Required for the User role; chosen from your active Work Roles.'],
              ['Workspaces', 'Required for a User — tick each workspace and, optionally, its Department.'],
              ['Status', 'Active or Suspended; a Suspension Reason field appears when Suspended (0–500 chars).'],
            ],
          },
        },

        { sub: 'Configuration › Reminders & Schedule (work_reports.view)' },
        { image: '/manual/ca-reminders-schedule.png', caption: 'Configuration › Reminders & Schedule — task reminders and the company report schedule.' },
        'Turn Daily, Weekly and Monthly report reminders on or off and set their timing. Each schedule is a card with its own enable toggle. A floating Save Changes / Reset pill appears once you edit anything.',
        {
          list: [
            'Daily card: enable toggle, Active days (Sun–Sat strip, at least one), Reminder at (time), and a "remind 24 hours after a miss" toggle.',
            'Weekly card: enable toggle, Day of week, Reminder at, and a "notify admins 48 hours after a miss" toggle.',
            'Monthly card: enable toggle, Day of month (1–28), Reminder at, "Report covers" (Last month / Current month), and a "notify admins 3 days after a miss" toggle.',
            'Task reminders: a ladder of pre-due offsets (e.g. 1 day before, 2 hours before) with add / remove per row.',
          ],
        },
        {
          note: 'Times use the company timezone. The Task Type and per-department Item Status catalogues used inside reports are configured here / in Work Report Settings. Set them up before you expect meaningful "completed" rollups.',
        },

        { sub: 'Process › SOPs Library (sops.manage)' },
        { image: '/manual/ca-sops-library.png', caption: 'Process › SOPs Library — publish and manage standard operating procedures.' },
        'Publish and manage Standard Operating Procedures (available if your plan allows). Open an SOP to read it or, where allowed, expand it into a reviewable task tree that can be saved as a reusable blueprint.',

        { sub: 'Process › Task Monitor (task_monitor.view)' },
        { image: '/manual/ca-task-monitor.png', caption: 'Process › Task Monitor — a company-wide view of every task with filters and inline edits.' },
        'Your live view of every task across the company, grouped into colour-coded status sections. List, Board and Gantt (timeline) views.',
        {
          list: [
            'List / Board / Gantt view toggle.',
            'Filters: search by Task Name, Space or List, plus Status, Department, Assignee, Priority and Repeat dropdowns, an Activity chip (Today / Yesterday / This week / This month / custom range — keeps tasks created or running in that window) and a Due date-range chip; Clear resets them.',
            'Status sections use your Space workflow colours; due dates are colour-coded — overdue in red, due today in amber, completed on time in green.',
            'List columns: Task Name, Department, Space, Assignees, Start Date, Due Date, Priority, Comments, Created By. Subtasks expand under their parent.',
            'Export CSV — downloads the tasks matching the current filters, with assignee names.',
          ],
        },

        { sub: 'Analytics (analytics.view)' },
        'A company-wide progress dashboard, shown when you hold analytics.view: KPI cards, a Task Status donut and an employee-wise table, all scoped to your company.',
        {
          list: [
            'Filters: Department and a Period selector (All time / Today / This week / This month / custom range). "Today" scopes to tasks created or running today.',
            'KPI cards: Employees, Tasks, Complete, To Do, In Progress, Overdue, Goals completed, Reports submitted and Verification pending.',
            'Task Status donut splits every task into To Do, In Progress, Complete and Overdue, coloured to match your Space workflow.',
            'Employee table shows per-person Assigned / Completed / Open / Overdue / Goals / Reports, with search, sortable columns and pagination.',
            'Export — download the table (CSV, Excel and more) honouring the active filters; employee name and email are included.',
          ],
        },

        { sub: 'Process › Work Reports (work_reports.view)' },
        { image: '/manual/ca-work-reports.png', caption: 'Process › Work Reports — review submitted Daily / Weekly / Monthly reports with compliance rings.' },
        'Audit every submitted report by user, date, workspace and status, and read the Daily / Weekly / Monthly compliance rings. Reported hours include task time plus meeting durations.',

        { sub: 'Files & Links' },
        { image: '/manual/ca-files-links.png', caption: 'Files & Links — the company-wide library of files and links.' },
        'The company’s shared library of files and links. Includes search, per-file actions (download, rename, delete) and a storage-usage card.',

        { sub: 'AI Usage (ai_usage.view)' },
        { image: '/manual/ca-ai-usage.png', caption: 'AI Usage — AI credit consumption across the company.' },
        'This month’s AI token spend against your plan’s monthly quota. Includes a recent-calls table (time, provider/model, tokens, feature) and refreshes automatically.',

        { sub: 'Logs › Audit / Login Activity / Email Status (audit_log.view)' },
        { image: '/manual/ca-logs.png', caption: 'Logs — the Audit, Login Activity and Email Status trails for your company.' },
        'Three read-only investigative logs.',
        {
          list: [
            'Audit Log: who changed what (create / update / delete), by resource, with a timestamp.',
            'Login Activity: logins with IP address and online/offline status.',
            'Email Status Logs: outbound email delivery (sent / failed), with a failure reason and a Retry action.',
          ],
        },

        { sub: 'Profile (from the user menu)' },
        { image: '/manual/ca-profile.png', caption: 'Profile — your personal details and password.' },
        'Your personal profile, reached from your avatar menu rather than the sidebar.',
        {
          list: [
            'Personal: avatar upload (JPEG/PNG/GIF/WebP, ≤2 MB), Full Name, Contact Number.',
            'Change password: Current, New (8–64 chars incl. upper, lower, number and symbol) and Confirm.',
          ],
        },
        {
          note: 'You also keep the standard member pages: your own Daily/Weekly/Monthly Reports, and Notifications at the header bell. Billing, AI Provider Keys and SOP Integration are never shown to a Sub Admin and are blocked on the server. Company-Admin-only areas (e.g. Gamification configuration) are hidden too. You can change the company country and timezone in My Preferences → Region & formats.',
        },
      ],
    },
    {
      id: 'daily-workflow',
      title: 'Daily Workflow',
      blocks: [
        'Your day depends on what you have been granted. A common pattern:',
        {
          steps: [
            'Log in and review the dashboard panels available to you.',
            'If you hold task_monitor.view: work through At-Risk Tasks in Task Monitor.',
            'If you hold work_reports.view: chase Missing Reports Today and review compliance.',
            'If you hold members.manage: process member invites and changes.',
            'If you hold audit_log.view: check the Logs when investigating anything.',
          ],
        },
      ],
    },
    {
      id: 'task-management',
      title: 'Task Management',
      blocks: [
        'The task model is the same as the rest of the company: Space → Project → List → Task → Subtask, with statuses defined per department on Workflows. What differs for you is reach: you can act on the areas your grants cover.',
        'With workspaces.manage you can browse the whole Space tree. With task_monitor.view you can see and filter every task. With sops.manage you can manage SOPs and their blueprints. Delegation still follows the seniority order for any Work Role you also hold as a member.',
        { sub: 'Task accountability & verification' },
        'Tasks can carry a Checker, an Expected output and a Proof-required flag, all set in the task detail. A task with a Checker is completed only when the Checker approves it. If you hold task_monitor.view, use the Verification filter on Task Monitor (/task-monitor) to see where each task stands with its checker across the company — Verified, Awaiting checker, Rejected, or No verification. As a Company/Sub Admin you may also act as a fallback approver.',
      ],
    },
    {
      id: 'reports',
      title: 'Reports',
      blocks: [
        'With work_reports.view you can open Reminders & Schedule and Work Reports: configure schedules, reminders, Task Types and per-department Item Statuses, add recipients, and audit submitted reports with their compliance rings. Without that grant, you still file your own Daily/Weekly/Monthly reports like any member.',
      ],
    },
    {
      id: 'notifications',
      title: 'Notifications',
      blocks: [
        'You receive the same personal notifications as any member (assignments, mentions, due-soon and overdue), plus activity for the administrative areas you manage — for example support-ticket updates if you hold support.manage. Everything arrives at the header bell.',
      ],
    },
    {
      id: 'faq',
      title: 'Frequently Asked Questions',
      blocks: [
        { sub: 'A menu is missing.' },
        'You have not been granted the operation that unlocks it. Ask your Company Admin to tick it on your member record.',
        { sub: 'I clicked a link and got "not allowed".' },
        'It is an owner-only area (Billing, AI Provider Keys, SOP Integration), or an operation you do not hold. These are blocked on the server, not just hidden.',
        { sub: 'My dashboard is empty.' },
        'You hold no operations yet. Your Company Admin needs to grant at least one.',
      ],
    },
    {
      id: 'best-practices',
      title: 'Best Practices',
      blocks: [
        {
          list: [
            'Confirm with your Company Admin exactly which operations you hold, so you know your remit.',
            'Work within your granted areas. Do not try to reach owner-only pages by URL — the server blocks them.',
            'When you need access outside your grants, request the specific operation rather than a broad promotion.',
            'Treat member and report data with the same care a Company Admin would — you are acting on their behalf.',
          ],
        },
      ],
    },
  ],
}

export default manual
